import React, { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import SmartSearchBox, { SmartSearchAnswer } from '../../search/SmartSearchBox';
import { ClothingBrowse } from '../../../hooks/useClothingBrowse';
import { BrowseFilters, cleanBrand } from '../../../lib/clothingBrowseApi';
import { ClothingFilters, SmartSearchTurn, askClothingSearch } from '../../../lib/smartSearchApi';

const HEADING = 'Not sure what to choose?';
const HELP_TEXT = "Tell us what it's for and who'll wear it. We'll set the filters for you.";
const PLACEHOLDER = 'e.g. navy polos for my café staff';
const EXAMPLES = ['Navy polos for staff', 'Warm jackets under £20', 'Hi-vis for site work'];
const STORAGE_KEY = 'oc_smart_search_clothing';
const MAX_LISTED = 3;

const GENDER_LABELS: Record<string, string> = { Male: 'Men', Female: 'Women' };

const money = (n: number): string => `£${Number.isInteger(n) ? n : n.toFixed(2)}`;

const list = (items: string[]): string =>
  items.length > MAX_LISTED ? `${items.slice(0, MAX_LISTED).join(', ')} +${items.length - MAX_LISTED}` : items.join(', ');

function priceLabel(min: number | null, max: number | null): string | null {
  if (min !== null && max !== null) return `${money(min)}–${money(max)}`;
  if (max !== null) return `under ${money(max)}`;
  if (min !== null) return `from ${money(min)}`;
  return null;
}

/** "Polos · Women · Blue · under £20 · 50 styles" */
function summarise(f: ClothingFilters, total: number | null): string {
  const parts: string[] = [];
  if (f.types.length) parts.push(list(f.types));
  if (f.genders.length) parts.push(list(f.genders.map((g) => GENDER_LABELS[g] || g)));
  if (f.ages.length) parts.push(list(f.ages));
  if (f.colours.length) parts.push(list(f.colours));
  if (f.brands.length) parts.push(list(f.brands.map(cleanBrand)));
  if (f.sizes.length) parts.push(`Size ${list(f.sizes)}`);
  const price = priceLabel(f.min, f.max);
  if (price) parts.push(price);
  if (f.eco) parts.push('Eco');
  if (f.q.trim()) parts.push(`“${f.q.trim()}”`);
  if (total !== null) parts.push(`${total.toLocaleString('en-GB')} ${total === 1 ? 'style' : 'styles'}`);
  return parts.length ? parts.join(' · ') : 'Showing all styles';
}

const toCurrent = (f: BrowseFilters): ClothingFilters => ({
  types: f.types,
  genders: f.genders,
  ages: f.ages,
  colours: f.colours,
  brands: f.brands,
  sizes: f.sizes,
  min: f.min ?? null,
  max: f.max ?? null,
  eco: f.eco,
  q: f.q,
  sort: f.sort,
});

const toPatch = (f: ClothingFilters): Partial<BrowseFilters> => ({
  types: f.types,
  genders: f.genders,
  ages: f.ages,
  colours: f.colours,
  brands: f.brands,
  sizes: f.sizes,
  min: f.min ?? undefined,
  max: f.max ?? undefined,
  eco: f.eco,
  q: f.q,
  sort: f.sort,
  page: 1,
});

interface ClothingSmartSearchProps {
  browse: ClothingBrowse;
}

const ClothingSmartSearch: React.FC<ClothingSmartSearchProps> = ({ browse }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Query string from just before the last applied search, for Undo
  const undoSearch = useRef<string | null>(null);
  // Decided on first render only: a search applied later must not clear the box.
  const [startFresh] = useState(() => browse.activeCount === 0 && !browse.filters.q);

  const onAsk = async (text: string, history: SmartSearchTurn[], signal: AbortSignal): Promise<SmartSearchAnswer> => {
    const result = await askClothingSearch(
      { messages: [...history, { role: 'user', content: text }], current: toCurrent(browse.filters) },
      signal,
    );

    // Analytics only if the site's PostHog client is running (it needs REACT_APP_POSTHOG_KEY)
    if (posthog.__loaded) {
      posthog.capture('smart_search', {
        scope: 'clothing',
        query: text,
        total: result.total,
        relaxed: result.relaxed,
        applied: !!result.filters,
      });
    }

    if (!result.filters) return { reply: result.reply };

    undoSearch.current = window.location.search;
    browse.update(toPatch(result.filters));
    return { reply: result.reply, summary: summarise(result.filters, result.total) };
  };

  const onUndo = useCallback(() => {
    if (undoSearch.current === null) return;
    navigate({ pathname, search: undoSearch.current });
    undoSearch.current = null;
  }, [navigate, pathname]);

  return (
    <SmartSearchBox
      heading={HEADING}
      helpText={HELP_TEXT}
      summaryLabel="Filters set"
      inputId="clothing-smart-search"
      className="mb-4"
      placeholder={PLACEHOLDER}
      examples={EXAMPLES}
      onAsk={onAsk}
      onUndo={onUndo}
      storageKey={STORAGE_KEY}
      startFresh={startFresh}
    />
  );
};

export default ClothingSmartSearch;
